import * as ko from 'knockout';
import { CfbTeam } from './models/team';
import { CfbPlayoffTeam } from './models/playoff-team';
import { SelectionType } from '../shared/enums/selection-type';
import { Enumerable } from '../shared/linq';
import { SchedulePickerBase } from '../shared/schedule-picker-base';
import { getWebAppBaseUrl } from '../shared/config';

export { CfbPlayoffsVM, CfbPlayoffGame };

class CfbPlayoffGame {
  Home?: CfbPlayoffTeam;
  Away?: CfbPlayoffTeam;
  SaveIndex: number;
  TeamSelected: KnockoutObservable<CfbPlayoffTeam>;
  TeamThatLost: KnockoutComputed<CfbPlayoffTeam>;
  SelectedTeamCss: KnockoutComputed<string>;
  ParentVM: CfbPlayoffsVM;
  Conference: string;
  Round: string;
  linkedGame: string;

  constructor(
    away: CfbPlayoffTeam,
    home: CfbPlayoffTeam,
    conference: string,
    round: string,
    saveIndex: number,
    linkedGame: string,
    parentVM: CfbPlayoffsVM,
  ) {
    const self = this;
    self.linkedGame = linkedGame;
    self.SaveIndex = saveIndex;
    self.Away = away;
    self.Home = home;
    self.Conference = conference;
    self.Round = round;
    self.TeamSelected = ko.observable(null);
    self.TeamThatLost = ko.computed(() => {
      if (self.TeamSelected()) {
        return self.Away === self.TeamSelected() ? self.Home : self.Away;
      }
      return null;
    });
    self.SelectedTeamCss = ko.computed(() => {
      if (self.TeamSelected?.() && self.TeamSelected().Team?.TeamAbbreviation) {
        return self.TeamSelected().Team.SelectedCss();
      }
      return '';
    });
    self.ParentVM = parentVM;
  }

  pickTeam(team: CfbPlayoffTeam, skip?: boolean) {
    const self = this;
    if (self.TeamSelected() === team) {
      return self.unselectTeam(team);
    }
    self.TeamSelected(team);
    self.ParentVM.clearLink(self.linkedGame, self.TeamThatLost());

    switch (self.Round) {
      case 'firstround': {
        self.ParentVM.PlayoffGames()[self.linkedGame].Away = team;
        self.ParentVM.PlayoffGames.valueHasMutated();
        break;
      }
      case 'quarterfinals': {
        const qf1 = self.ParentVM.PlayoffGames()['quarterfinals-1'];
        const qf2 = self.ParentVM.PlayoffGames()['quarterfinals-2'];
        const qf3 = self.ParentVM.PlayoffGames()['quarterfinals-3'];
        const qf4 = self.ParentVM.PlayoffGames()['quarterfinals-4'];
        if (qf1.TeamSelected()) self.ParentVM.PlayoffGames()['semifinals-1'].Away = qf1.TeamSelected();
        if (qf2.TeamSelected()) self.ParentVM.PlayoffGames()['semifinals-1'].Home = qf2.TeamSelected();
        if (qf3.TeamSelected()) self.ParentVM.PlayoffGames()['semifinals-2'].Away = qf3.TeamSelected();
        if (qf4.TeamSelected()) self.ParentVM.PlayoffGames()['semifinals-2'].Home = qf4.TeamSelected();
        self.ParentVM.PlayoffGames()['finals-1'].TeamSelected(null);
        self.ParentVM.PlayoffGames.valueHasMutated();
        // falls through intentionally
      }
      case 'semifinals': {
        const sf1 = self.ParentVM.PlayoffGames()['semifinals-1'];
        const sf2 = self.ParentVM.PlayoffGames()['semifinals-2'];
        if (sf1.TeamSelected()) self.ParentVM.PlayoffGames()['finals-1'].Away = sf1.TeamSelected();
        if (sf2.TeamSelected()) self.ParentVM.PlayoffGames()['finals-1'].Home = sf2.TeamSelected();
        self.ParentVM.PlayoffGames()['finals-1'].TeamSelected(null);
        self.ParentVM.PlayoffGames.valueHasMutated();
        break;
      }
      case 'finals': {
        self.ParentVM.PlayoffGames.valueHasMutated();
        break;
      }
      default:
        console.warn(self.Round);
    }
    self.ParentVM.SaveValues();
  }

  unselectTeam(team: CfbPlayoffTeam) {
    this.ParentVM.clearLink(this.linkedGame, team);
    this.ParentVM.PlayoffGames.valueHasMutated();
  }
}

class CfbPlayoffsVM {
  SuperbowlYear: number;
  LeagueName: string;
  AllTeams: KnockoutObservableArray<CfbTeam>;
  PlayoffGames: KnockoutObservable<Record<string, CfbPlayoffGame>>;
  SaveValues: KnockoutComputed<string>;
  Loading: KnockoutObservable<boolean>;
  Version: number;

  SelectedSeed1: KnockoutObservable<CfbTeam>;
  SelectedSeed2: KnockoutObservable<CfbTeam>;
  SelectedSeed3: KnockoutObservable<CfbTeam>;
  SelectedSeed4: KnockoutObservable<CfbTeam>;
  SelectedSeed5: KnockoutObservable<CfbTeam>;
  SelectedSeed6: KnockoutObservable<CfbTeam>;
  SelectedSeed7: KnockoutObservable<CfbTeam>;
  SelectedSeed8: KnockoutObservable<CfbTeam>;
  SelectedSeed9: KnockoutObservable<CfbTeam>;
  SelectedSeed10: KnockoutObservable<CfbTeam>;
  SelectedSeed11: KnockoutObservable<CfbTeam>;
  SelectedSeed12: KnockoutObservable<CfbTeam>;

  Seed1: KnockoutObservable<CfbPlayoffTeam>;
  Seed2: KnockoutObservable<CfbPlayoffTeam>;
  Seed3: KnockoutObservable<CfbPlayoffTeam>;
  Seed4: KnockoutObservable<CfbPlayoffTeam>;
  Seed5: KnockoutObservable<CfbPlayoffTeam>;
  Seed6: KnockoutObservable<CfbPlayoffTeam>;
  Seed7: KnockoutObservable<CfbPlayoffTeam>;
  Seed8: KnockoutObservable<CfbPlayoffTeam>;
  Seed9: KnockoutObservable<CfbPlayoffTeam>;
  Seed10: KnockoutObservable<CfbPlayoffTeam>;
  Seed11: KnockoutObservable<CfbPlayoffTeam>;
  Seed12: KnockoutObservable<CfbPlayoffTeam>;

  FinalsWinners: KnockoutComputed<CfbPlayoffTeam[]>;

  constructor(teams: CfbTeam[]) {
    const self = this;
    self.Loading = ko.observable(true);
    self.PlayoffGames = ko.observable({} as Record<string, CfbPlayoffGame>);

    const games: Record<string, CfbPlayoffGame> = {};
    games['firstround-1'] = new CfbPlayoffGame(null, null, '', 'firstround', 0, 'quarterfinals-1', self);
    games['firstround-2'] = new CfbPlayoffGame(null, null, '', 'firstround', 1, 'quarterfinals-2', self);
    games['firstround-3'] = new CfbPlayoffGame(null, null, '', 'firstround', 2, 'quarterfinals-3', self);
    games['firstround-4'] = new CfbPlayoffGame(null, null, '', 'firstround', 3, 'quarterfinals-4', self);
    games['quarterfinals-1'] = new CfbPlayoffGame(null, null, '', 'quarterfinals', 4, 'semifinals-1', self);
    games['quarterfinals-2'] = new CfbPlayoffGame(null, null, '', 'quarterfinals', 5, 'semifinals-1', self);
    games['quarterfinals-3'] = new CfbPlayoffGame(null, null, '', 'quarterfinals', 6, 'semifinals-2', self);
    games['quarterfinals-4'] = new CfbPlayoffGame(null, null, '', 'quarterfinals', 7, 'semifinals-2', self);
    games['semifinals-1'] = new CfbPlayoffGame(null, null, '', 'semifinals', 8, 'finals-1', self);
    games['semifinals-2'] = new CfbPlayoffGame(null, null, '', 'semifinals', 9, 'finals-1', self);
    games['finals-1'] = new CfbPlayoffGame(null, null, '', 'finals', 10, null, self);
    self.PlayoffGames(games);

    self.AllTeams = ko.observableArray(teams);

    // Helper to wire seed observable pair
    const wireSeed = (
      selectedObs: KnockoutObservable<CfbTeam>,
      seedObs: KnockoutObservable<CfbPlayoffTeam>,
      seedNum: number,
      onSeedChange: (team: CfbPlayoffTeam) => void,
    ) => {
      selectedObs.subscribe((selectedTeam) => {
        const team = new CfbPlayoffTeam(selectedTeam, seedNum);
        seedObs(team);
      });
      seedObs.subscribe(onSeedChange);
    };

    self.SelectedSeed1 = ko.observable();
    self.Seed1 = ko.observable();
    wireSeed(self.SelectedSeed1, self.Seed1, 1, (team) => {
      if (self.PlayoffGames()['quarterfinals-2'].Home) {
        self.PlayoffGames()['quarterfinals-2'].Home = team;
        self.clearLink(self.PlayoffGames()['quarterfinals-2'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed2 = ko.observable();
    self.Seed2 = ko.observable();
    wireSeed(self.SelectedSeed2, self.Seed2, 2, (team) => {
      if (self.PlayoffGames()['quarterfinals-4'].Home) {
        self.PlayoffGames()['quarterfinals-4'].Home = team;
        self.clearLink(self.PlayoffGames()['quarterfinals-4'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed3 = ko.observable();
    self.Seed3 = ko.observable();
    wireSeed(self.SelectedSeed3, self.Seed3, 3, (team) => {
      if (self.PlayoffGames()['quarterfinals-3'].Home) {
        self.PlayoffGames()['quarterfinals-3'].Home = team;
        self.clearLink(self.PlayoffGames()['quarterfinals-3'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed4 = ko.observable();
    self.Seed4 = ko.observable();
    wireSeed(self.SelectedSeed4, self.Seed4, 4, (team) => {
      if (self.PlayoffGames()['quarterfinals-1'].Home) {
        self.PlayoffGames()['quarterfinals-1'].Home = team;
        self.clearLink(self.PlayoffGames()['quarterfinals-1'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed5 = ko.observable();
    self.Seed5 = ko.observable();
    wireSeed(self.SelectedSeed5, self.Seed5, 5, (team) => {
      if (self.PlayoffGames()['firstround-1'].Home) {
        self.PlayoffGames()['firstround-1'].Home = team;
        self.clearLink(self.PlayoffGames()['firstround-1'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed6 = ko.observable();
    self.Seed6 = ko.observable();
    wireSeed(self.SelectedSeed6, self.Seed6, 6, (team) => {
      if (self.PlayoffGames()['firstround-3'].Home) {
        self.PlayoffGames()['firstround-3'].Home = team;
        self.clearLink(self.PlayoffGames()['firstround-3'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed7 = ko.observable();
    self.Seed7 = ko.observable();
    wireSeed(self.SelectedSeed7, self.Seed7, 7, (team) => {
      if (self.PlayoffGames()['firstround-4'].Home) {
        self.PlayoffGames()['firstround-4'].Home = team;
        self.clearLink(self.PlayoffGames()['firstround-4'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed8 = ko.observable();
    self.Seed8 = ko.observable();
    wireSeed(self.SelectedSeed8, self.Seed8, 8, (team) => {
      if (self.PlayoffGames()['firstround-2'].Home) {
        self.PlayoffGames()['firstround-2'].Home = team;
        self.clearLink(self.PlayoffGames()['firstround-2'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed9 = ko.observable();
    self.Seed9 = ko.observable();
    wireSeed(self.SelectedSeed9, self.Seed9, 9, (team) => {
      if (self.PlayoffGames()['firstround-2'].Away) {
        self.PlayoffGames()['firstround-2'].Away = team;
        self.clearLink(self.PlayoffGames()['firstround-2'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed10 = ko.observable();
    self.Seed10 = ko.observable();
    wireSeed(self.SelectedSeed10, self.Seed10, 10, (team) => {
      if (self.PlayoffGames()['firstround-4'].Away) {
        self.PlayoffGames()['firstround-4'].Away = team;
        self.clearLink(self.PlayoffGames()['firstround-4'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed11 = ko.observable();
    self.Seed11 = ko.observable();
    wireSeed(self.SelectedSeed11, self.Seed11, 11, (team) => {
      if (self.PlayoffGames()['firstround-3'].Away) {
        self.PlayoffGames()['firstround-3'].Away = team;
        self.clearLink(self.PlayoffGames()['firstround-3'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SelectedSeed12 = ko.observable();
    self.Seed12 = ko.observable();
    wireSeed(self.SelectedSeed12, self.Seed12, 12, (team) => {
      if (self.PlayoffGames()['firstround-1'].Away) {
        self.PlayoffGames()['firstround-1'].Away = team;
        self.clearLink(self.PlayoffGames()['firstround-1'].linkedGame, team);
      }
      self.PlayoffGames.valueHasMutated();
    });

    self.SaveValues = ko.computed(() => {
      if (self.Loading()) return '';
      const retStr: number[] = [];
      for (const gameIdx in self.PlayoffGames()) {
        const game = self.PlayoffGames()[gameIdx];
        if (game.Away && game.Home && game.TeamSelected()) {
          retStr[game.SaveIndex] = game.Away.Team.TeamID === game.TeamSelected().Team.TeamID ? 2 : 1;
        } else {
          retStr[game.SaveIndex] = 0;
        }
      }
      const returnString = retStr.join('');
      const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?L=${returnString}` +
        `&s1=${self.SelectedSeed1()?.TeamID}&s2=${self.SelectedSeed2()?.TeamID}` +
        `&s3=${self.SelectedSeed3()?.TeamID}&s4=${self.SelectedSeed4()?.TeamID}` +
        `&s5=${self.SelectedSeed5()?.TeamID}&s6=${self.SelectedSeed6()?.TeamID}` +
        `&s7=${self.SelectedSeed7()?.TeamID}&s8=${self.SelectedSeed8()?.TeamID}` +
        `&s9=${self.SelectedSeed9()?.TeamID}&s10=${self.SelectedSeed10()?.TeamID}` +
        `&s11=${self.SelectedSeed11()?.TeamID}&s12=${self.SelectedSeed12()?.TeamID}`;
      window.history.pushState({ path: newurl }, '', newurl);
      return newurl;
    });

    self.FinalsWinners = ko.computed(() => {
      const temp: CfbPlayoffTeam[] = [];
      const winner1 = self.PlayoffGames()['finals-1']?.TeamSelected();
      temp.push(winner1 ?? null);
      return Enumerable.From(temp).OrderByDescending((p: CfbPlayoffTeam) => p?.Seed ?? 0).ToArray();
    });

    const initialSaveString = SchedulePickerBase.getQueryVariable('L');
    self.load(initialSaveString);
  }

  load(saveString?: string) {
    const self = this;
    const loading = self.Loading();

    const getS = (key: string) => parseInt(SchedulePickerBase.getQueryVariable(key.toUpperCase()), 10);
    const s1 = getS('S1'), s2 = getS('S2'), s3 = getS('S3'), s4 = getS('S4'),
      s5 = getS('S5'), s6 = getS('S6'), s7 = getS('S7'), s8 = getS('S8'),
      s9 = getS('S9'), s10 = getS('S10'), s11 = getS('S11'), s12 = getS('S12');

    const randomSeeds = self.getRandomUniqueValues(113, 500, 12);

    const setSeed = (obs: KnockoutObservable<CfbTeam>, sid: number) => {
      if (sid) {
        obs(self.AllTeams().find((t) => t.TeamID === sid));
      } else {
        obs(self.AllTeams()[randomSeeds.pop()]);
      }
    };

    setSeed(self.SelectedSeed1, s1);
    setSeed(self.SelectedSeed2, s2);
    setSeed(self.SelectedSeed3, s3);
    setSeed(self.SelectedSeed4, s4);
    setSeed(self.SelectedSeed5, s5);
    setSeed(self.SelectedSeed6, s6);
    setSeed(self.SelectedSeed7, s7);
    setSeed(self.SelectedSeed8, s8);
    setSeed(self.SelectedSeed9, s9);
    setSeed(self.SelectedSeed10, s10);
    setSeed(self.SelectedSeed11, s11);
    setSeed(self.SelectedSeed12, s12);

    self.PlayoffGames()['firstround-1'].Away = self.Seed12();
    self.PlayoffGames()['firstround-1'].Home = self.Seed5();
    self.PlayoffGames()['firstround-2'].Away = self.Seed9();
    self.PlayoffGames()['firstround-2'].Home = self.Seed8();
    self.PlayoffGames()['firstround-3'].Away = self.Seed11();
    self.PlayoffGames()['firstround-3'].Home = self.Seed6();
    self.PlayoffGames()['firstround-4'].Away = self.Seed10();
    self.PlayoffGames()['firstround-4'].Home = self.Seed7();
    self.PlayoffGames()['quarterfinals-1'].Home = self.Seed4();
    self.PlayoffGames()['quarterfinals-2'].Home = self.Seed1();
    self.PlayoffGames()['quarterfinals-3'].Home = self.Seed3();
    self.PlayoffGames()['quarterfinals-4'].Home = self.Seed2();

    if (!saveString) {
      self.Loading(false);
      return;
    }

    const map: Record<number, CfbPlayoffGame> = {};
    for (const gameIdx in self.PlayoffGames()) {
      const game = self.PlayoffGames()[gameIdx];
      map[game.SaveIndex] = game;
    }
    for (let i = 0; i < saveString.length; i++) {
      const selection: SelectionType = parseInt(saveString[i]);
      const game = map[i];
      if (!game) continue;
      switch (selection) {
        case SelectionType.HOME_WIN: game.pickTeam(game.Home, loading); break;
        case SelectionType.AWAY_WIN: game.pickTeam(game.Away, loading); break;
        default: break;
      }
    }

    self.Loading(false);
  }

  export() {
    (window as any).html2canvas?.(document.querySelector('#playoffs table'), {
      background: '#FFF', useCORS: true, allowTaint: true,
    }).then((canvas: any) => {
      const output = document.getElementById('output');
      output.innerHTML = '';
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d')!;
      const text = 'Created With Playoff Predictors';
      const cw = tempCanvas.width = canvas.width;
      const ch = tempCanvas.height = canvas.height + 80;
      tempCtx.drawImage(canvas, 0, 0);
      tempCtx.font = '14px Helvetica';
      const textWidth = tempCtx.measureText(text).width;
      tempCtx.fillStyle = 'black';
      tempCtx.fillText(text, cw - textWidth - 30, ch - 40);
      const text2 = getWebAppBaseUrl();
      const textWidth2 = tempCtx.measureText(text2).width;
      tempCtx.fillStyle = 'black';
      tempCtx.fillText(text2, cw - textWidth2 - 30, ch - 20);
      const logo = new Image();
      logo.src = '/content/playoff-predictor-color-02.svg';
      logo.width = 64;
      logo.height = 64;
      logo.onload = function () {
        tempCtx.drawImage(logo, cw - textWidth2 - 100, ch - 60, logo.width * 0.75, logo.height * 0.75);
        output.innerHTML = '';
        output.appendChild(tempCanvas);
      };
    });
    document.querySelectorAll<HTMLInputElement>('input').forEach((el) => { el.style.display = ''; });
  }

  reset() {
    const self = this;
    for (const gameIdx in self.PlayoffGames()) {
      const game = self.PlayoffGames()[gameIdx];
      self.clearLink(game.linkedGame, game.Away);
      self.clearLink(game.linkedGame, game.Home);
      game.TeamSelected(null);
    }
    self.PlayoffGames.valueHasMutated();
    const output = document.getElementById('output');
    if (output) { output.innerHTML = ''; }
  }

  clearLink(linkedGameRound: string, team: CfbPlayoffTeam) {
    const self = this;
    if (linkedGameRound == null) return;
    const linkedGame = self.PlayoffGames()[linkedGameRound];
    if (linkedGame.Home === team) linkedGame.Home = null;
    if (linkedGame.Away === team) linkedGame.Away = null;
    if (linkedGame.Home !== team && linkedGame.Away !== team) {
      linkedGame.Home?.Seed === team?.Seed ? (linkedGame.Home = null) : (linkedGame.Away = null);
    }
    if (linkedGame.TeamSelected() === team) {
      linkedGame.TeamSelected(null);
      linkedGame.TeamSelected.valueHasMutated();
    }
    self.clearLink(linkedGame.linkedGame, team);
  }

  getRandomUniqueValues(minValue: number, maxValue: number, count: number): number[] {
    if (maxValue - minValue + 1 < count) {
      throw new Error('Range is too small to provide the required number of unique values.');
    }
    const uniqueValues = new Set<number>();
    while (uniqueValues.size < count) {
      const randomValue = Math.floor(Math.random() * (maxValue - minValue + 1)) + minValue;
      if (this.AllTeams()[randomValue]) {
        uniqueValues.add(randomValue);
      }
    }
    return Array.from(uniqueValues);
  }

  chaos() {
    const self = this;
    self.reset();
    const randomSeeds = self.getRandomUniqueValues(113, 500, 12);
    self.SelectedSeed1(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed2(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed3(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed4(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed5(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed6(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed7(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed8(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed9(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed10(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed11(self.AllTeams()[randomSeeds.pop()]);
    self.SelectedSeed12(self.AllTeams()[randomSeeds.pop()]);

    self.PlayoffGames()['firstround-1'].Away = self.Seed12();
    self.PlayoffGames()['firstround-1'].Home = self.Seed5();
    self.PlayoffGames()['firstround-2'].Away = self.Seed9();
    self.PlayoffGames()['firstround-2'].Home = self.Seed8();
    self.PlayoffGames()['firstround-3'].Away = self.Seed11();
    self.PlayoffGames()['firstround-3'].Home = self.Seed6();
    self.PlayoffGames()['firstround-4'].Away = self.Seed10();
    self.PlayoffGames()['firstround-4'].Home = self.Seed7();
    self.PlayoffGames()['quarterfinals-1'].Home = self.Seed4();
    self.PlayoffGames()['quarterfinals-2'].Home = self.Seed1();
    self.PlayoffGames()['quarterfinals-3'].Home = self.Seed3();
    self.PlayoffGames()['quarterfinals-4'].Home = self.Seed2();
  }
}
